import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Title,
  Textarea,
  Button,
  Avatar,
  Group,
  Drawer,
  Paper,
  Loader,
  Center,
  TextInput,
  ActionIcon,
} from "@mantine/core";
import fetchApi from "../components/Lib/api";
import ContainerButton from "./ContainerButton";
import { IconSend, IconTrash } from "@tabler/icons-react";
import useUserStore from "../stores/userStore";
import useStartupStore from "../stores/startupStore";
import Mixpanel from "../components/Lib/mixpanel";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

// Component: SubcategoryFeedback
const SubcategoryFeedback = ({ projectOwner, subCategoryId, subCategoryName }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFeedback, setNewFeedback] = useState("");
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerFeedback, setDrawerFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const {t}=useTranslation();

  const {
    user: { id, first_name, last_name, avatar_url },
  } = useUserStore((state) => state.user);

  const {
    isOwner,
    slug
  } = useStartupStore((state) => ({
    isOwner: state.isOwner,
    slug: state.slug
  }));

  // Fetch feedback for the subcategory on mount or when subCategoryId changes
  useEffect(() => {
    if (!subCategoryId) return;
    setLoading(true);
    fetchApi(`/sub_categories/${subCategoryId}/feedbacks`, "GET")
      .then(async (response) => {
        if (!response.ok) throw new Error("Error fetching feedback");
        const data = await response.json();
        setFeedbacks(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching feedback:", error);
        setLoading(false);
      });
  }, [subCategoryId]);

  // Function to submit feedback (used for both inline and drawer submission)
  const submitFeedback = async (feedbackText, onSuccess) => {
    if (!feedbackText.trim()) return;

    const startTime = performance.now();
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/feedbacks`,
        "POST",
        {
          feedback: {
            // We assume the backend will associate the feedback with the current user
            // so we don't need to send name/email if that is handled server-side.
            message: feedbackText,
            rating: 0,
          },
        }
      );

      const responseTime = Math.round(performance.now() - startTime);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.error ||
            (Array.isArray(payload?.errors) ? payload.errors.join(", ") : "Error submitting feedback")
        );
      }
      setFeedbackError("");
      console.log(payload);
      Mixpanel.content('Subcategory Feedback', 'Created', {
        sub_category_id: subCategoryId,
        sub_category_name: subCategoryName,
        feedback_text: feedbackText,
        response_time_ms: responseTime,
        startup_slug: slug
    });
      // Prepend the new feedback to the list so it appears first.
      setFeedbacks((prev) => [{message: feedbackText, user: {first_name, last_name, avatar_url,}}, ...prev]);
      if (onSuccess) onSuccess();
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      setFeedbackError(error.message);
      console.error("Error submitting feedback:", error);
      Mixpanel.error('Submit Feedback Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'SubcategoryFeedback',
        user_action: 'Submit Feedback',
        sub_category_id: subCategoryId,
        sub_category_name: subCategoryName,
        startup_slug: slug,
        response_time_ms: responseTime
      });
    }
  };

  // Function to delete feedback
  const deleteFeedback = async (feedbackId) => {
    const startTime = performance.now();
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/feedbacks/${feedbackId}`,
        "DELETE"
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) throw new Error("Error deleting feedback");
      
      Mixpanel.content('Subcategory Feedback', 'Deleted', {
        sub_category_id: subCategoryId,
        sub_category_name: subCategoryName,
        feedback_id: feedbackId,
        response_time_ms: responseTime,
        startup_slug: slug
      });
      
      // Remove the feedback from the list
      setFeedbacks((prev) => prev.filter(fb => fb.id !== feedbackId));
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error deleting feedback:", error);
      Mixpanel.error('Delete Feedback Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'SubcategoryFeedback',
        user_action: 'Delete Feedback',
        sub_category_id: subCategoryId,
        sub_category_name: subCategoryName,
        startup_slug: slug,
        response_time_ms: responseTime
      });
    }
  };

  const Comment = ({ fb }) => {
    const canDelete = isOwner || (fb.user && fb.user.id === id);
    
    return (
      <Group key={fb.id} mb="sm" position="apart">
        <Group spacing="xs">
          <Avatar
            src={fb.user && fb.user.avatar_url ? fb.user.avatar_url : null}
            radius="xl"
            size="md"
            color="blue"
          >
            {fb.user && fb.user.first_name
              ? `${fb.user.first_name?.charAt(0)}${fb.user.last_name?.charAt(0)}`
              : "A"}
          </Avatar>
          <Box>
            <Text size="sm" weight={500}>
              {fb.user
                ? `${fb.user.first_name} ${fb.user.last_name}`
                : "Anonymous"}
            </Text>
            <Text size="sm" color="dimmed">
              {fb.message}
            </Text>
          </Box>
        </Group>
        {canDelete && (
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            onClick={() => deleteFeedback(fb.id)}
            title="Delete comment"
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </Group>
    );
  };

  // Render first two feedback items
  const renderInlineFeedbacks = () => {
   
    if (feedbacks.length === 0) {
      return (
        <Text color="dimmed" size="sm" my="md">
         {t("comments.no_comments")}
        </Text>
      );
    }
    return feedbacks.slice(0, 2).map((fb) => <Comment fb={fb} />);
  };

  return (
    <Box>
      {loading ? (
        <Center>
          <Loader size="sm" />
        </Center>
      ) : (
        <>
          {/* Inline Feedback Display */}
          {renderInlineFeedbacks()}

          {/* Show "View more" if more than 2 feedback items exist */}
          {feedbacks.length > 2 && (
            <Button
              variant="subtle"
              onClick={() => setDrawerOpened(true)}
              compact
              mb="md"
            >
              {t("comments.view_more")}
            </Button>
          )}
        </>
      )}

      {/* Inline Textarea for feedback submission */}
      <Group align="center" gap="xs">
        <TextInput
          placeholder={t("comments.write_comments")}
          value={newFeedback}
          onChange={(e) => setNewFeedback(e.currentTarget.value)}
          radius="sm"
          style={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submitFeedback(newFeedback, () => setNewFeedback(""));
            }
          }}
        />
        <ActionIcon
          variant="filled"
          color="gray"
          radius="xl"
          size="lg"
          onClick={() => submitFeedback(newFeedback, () => setNewFeedback(""))}
        >
          <IconSend size={18} />
        </ActionIcon>
      </Group>
      {feedbackError && (
        <Text size="xs" color="red" mt="xs">
          {feedbackError}
        </Text>
      )}
      {/* Drawer for viewing all feedback and submitting additional feedback */}
      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        title={`${t("comments.all_comments")} ${subCategoryName}`}
        position="right"
        size="xl"
      >
        <Box>
          {feedbacks.length === 0 ? (
            <Text>{t("comments.no_comments")}</Text>
          ) : (
            feedbacks.map((fb) => (
              <Comment fb={fb} />
            ))
          )}

          <Textarea
            placeholder={`${t("comments.write_comments")} ${subCategoryName}...`}
            value={drawerFeedback}
            onChange={(e) => setDrawerFeedback(e.currentTarget.value)}
            autosize
            minRows={3}
            my="md"
            
          />
          {feedbackError && (
            <Text size="xs" color="red" mb="sm">
              {feedbackError}
            </Text>
          )}
          <ContainerButton
            action={() =>
              submitFeedback(drawerFeedback, () => setDrawerFeedback(""))
            }
            buttonText={t("comments.post_button")}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

export default SubcategoryFeedback;
